import { get, post } from "./httpClient";

export interface DesiderataQuotas {
  totalWeekends: number;
  weekendsWithPublicHolidays: number;
  netWeekends: number;
  allowedWeekendDesiderata: number;
  totalWorkingDays: number;
  workingDaysWithPublicHolidays: number;
  netWorkingDays: number;
  allowedWorkingDayDesiderata: number;
}

export interface DesiderataUsage {
  weekendsUsed: number;
  workingDaysUsed: number;
  weekendsRemaining: number;
  workingDaysRemaining: number;
}

export interface DesiderataQuotaInfo {
  periodId: string;
  periodName: string;
  quotas: DesiderataQuotas;
  usage: DesiderataUsage;
  lastUpdated: string;
}

export interface DesiderataValidation {
  valid: boolean;
  weekendsUsed: number;
  workingDaysUsed: number;
  weekendsAllowed: number;
  workingDaysAllowed: number;
  weekendsRemaining: number;
  workingDaysRemaining: number;
  error?: string;
}

/**
 * Get desiderata quota information for a period
 */
export async function getDesiderataQuota(
  periodId: string
): Promise<DesiderataQuotaInfo> {
  return get<DesiderataQuotaInfo>(`/desiderata/quota/${periodId}`, { cacheResponse: 5 });
}

/**
 * Validate a desiderata request
 */
export async function validateDesiderata(
  periodId: string,
  startDate: string,
  endDate: string,
  excludeEventId?: string
): Promise<DesiderataValidation> {
  return post<DesiderataValidation>("/desiderata/validate", {
    periodId,
    startDate,
    endDate,
    excludeEventId,
  }, { cacheResponse: false });
}

/**
 * Recalculate desiderata usage for a user
 */
export async function recalculateDesiderata(
  token: string,
  userId: string,
  periodId: string
): Promise<void> {
  const response = await fetch(
    `${API_URL}/desiderata/recalculate/${userId}/${periodId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to recalculate desiderata" }));
    throw new Error(error.message);
  }
}

export interface PendingDesiderataGridItem {
  date: string;
  [userId: string]: string | number;
  total: number;
}

export interface PendingDesiderataResponse {
  site: string;
  year: string;
  periodId: string;
  count: number;
  requests: any[];
  desiderata?: any[];
  grid?: PendingDesiderataGridItem[];
}

/**
 * Get pending desiderata for a specific year and period
 */
export async function getPendingDesiderata(
  token: string,
  year: string,
  periodId: string
): Promise<PendingDesiderataResponse> {
  const response = await fetch(
    `${API_URL}/desiderata/pending/${year}/${periodId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to fetch pending desiderata" }));
    throw new Error(error.message);
  }

  return response.json();
}
